import React, { useEffect, useState } from 'react';
import SelectedCard from './SelectedCards';
import { t } from 'i18next';
import { styled } from '@mui/material/styles';
import { GroupTypes } from 'app/pages/team/groups/utils';
import type { GetCardsPayload } from 'CDK_API';
import { sentryMask } from 'sentryReplayPrivacy';
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

interface SelectMultipleCardsProps {
  field: {
    onChange: (value: string[]) => void;
  };
  options: Array<GetCardsPayload>;
  selectedOptions?: Array<GetCardsPayload>;
  onChange?: (selectedOptions: Array<string>) => void;
  onOpen?: () => void;
  onInput?: (searchTerm: string) => void;
  loading?: boolean;
  placeholder?: string;
}

const Container = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  padding: theme.spacing(2),
  background: 'var(--Blues-Highlight-Blue, #F2F6FF)',
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const CardHolder = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  minHeight: '52px',
  borderRadius: theme.shape.borderRadius,
  gap: theme.spacing(0.5),
  padding: theme.spacing(2),
  overflow: 'auto',
}));
//select multiple cards (paymentCardId)
const SelectMultipleCards: React.FC<SelectMultipleCardsProps> = ({
  field,
  options,
  selectedOptions,
  onChange,
  onOpen,
  onInput,
  loading,
  placeholder,
}) => {
  const [selectedCards, setSelectedCards] = useState<Array<GetCardsPayload>>(selectedOptions || []);
  const [inputValue, setInputValue] = useState<string>('');
  const availableOptions = options.filter(
    (option) => !selectedCards.some((selected) => selected.paymentCardId === option.paymentCardId)
  );

  const handleDelete = (option: GetCardsPayload) => {
    const updatedCards = selectedCards.filter((card) => card.paymentCardId !== option.paymentCardId);
    setSelectedCards(updatedCards);

    if (onChange) {
      onChange(updatedCards.map((card) => card.paymentCardId));
    }
    field.onChange(updatedCards.map((card) => card.paymentCardId));
  };

  useEffect(() => {
    setSelectedCards(selectedOptions || []);
  }, [selectedOptions]);

  return (
    <Container>
      <Title variant="h4">{t('cards')}</Title>
      <CardHolder>
        <Autocomplete
          id="select-multiple-cards"
          multiple
          filterOptions={(x) => x}
          inputValue={inputValue}
          onOpen={
            onOpen
              ? () => {
                  setInputValue('');
                  onOpen();
                }
              : undefined
          }
          isOptionEqualToValue={(option, value) => option.paymentCardId === value.paymentCardId}
          options={availableOptions}
          disablePortal={true}
          getOptionLabel={(option) => `*${option.last4} `}
          value={selectedCards}
          disableCloseOnSelect={true}
          onChange={(event, newValue) => {
            setSelectedCards(newValue);
            setInputValue(''); // Clear input value after selecting a card
            // Reload all cards so user can select another without searching
            if (onInput) {
              onInput('');
            }
            field.onChange(newValue.map((card) => card.paymentCardId));
            if (onChange) {
              onChange(newValue.map((card) => card.paymentCardId));
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            setInputValue(newInputValue);
            if (reason === 'input' || reason === 'clear') {
              if (onInput) {
                onInput(newInputValue);
              }
            }
          }}
          loading={loading}
          renderTags={(value: GetCardsPayload[], getTagProps) =>
            value.map((option: GetCardsPayload, index: number) => {
              return (
                <SelectedCard
                  key={option.paymentCardId ?? index}
                  card={option}
                  groupType={GroupTypes.CardGroups}
                  onClose={() => {
                    handleDelete(option);
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={placeholder || t('typeToSearch')}
              inputProps={{ ...params.inputProps, ...sentryMask }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <InputAdornment position="end">
                        <CircularProgress color="inherit" size={18} />
                      </InputAdornment>
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                disableUnderline: true,
              }}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <MenuItem {...props} key={option.paymentCardId} value={option.paymentCardId}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography sx={{ minWidth: 50 }}>{`*${option.last4} `}</Typography>
                <Avatar
                  sx={{ maxWidth: 24, maxHeight: 24, fontSize: '1em' }}
                  src={option.cardholder?.picture || undefined}
                  alt={(option.cardholder?.name || option.name) ?? undefined}
                  {...sentryMask}
                />
                <Typography {...sentryMask}>
                  {(option.cardholder?.name || option.name) ?? t('unassigned')}
                </Typography>
              </Box>
            </MenuItem>
          )}
        />
      </CardHolder>
    </Container>
  );
};

export default SelectMultipleCards;
